import React, { useEffect, useState } from 'react';
import {
  PhoneIcon,
  XMarkIcon,
  MicrophoneIcon,
  PhoneXMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { MicrophoneIcon as MicrophoneSolidIcon } from '@heroicons/react/24/solid';
import { campaignService } from '../../../services/campaignService';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { useOptionalWebRTC } from '../../../contexts/WebRTCContext';

interface Props {
  fromNumber: string;
  onClose: () => void;
}

type CallPhase = 'idle' | 'ringing' | 'in-call' | 'ended';

const formatDuration = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const TestCallModal: React.FC<Props> = ({ fromNumber, onClose }) => {
  const [toNumber, setToNumber] = useState('');
  const [phase, setPhase] = useState<CallPhase>('idle');

  const webrtc = useOptionalWebRTC();
  const status = webrtc?.status;
  const isMuted = webrtc?.isMuted ?? false;
  const isTestCall = webrtc?.isTestCall ?? false;
  const seconds = webrtc?.seconds ?? 0;

  useEffect(() => {
    if (phase === 'ringing' && isTestCall && status === 'in-call') {
      setPhase('in-call');
    } else if ((phase === 'ringing' || phase === 'in-call') && !isTestCall && status === 'ready') {
      setPhase('ended');
    }
  }, [status, isTestCall, phase]);

  const handleHttpFallback = async (cleaned: string) => {
    setPhase('ringing');
    try {
      const result = await campaignService.placeTestCall(fromNumber, cleaned);
      if (result.success) {
        toast.success(result.data.message || 'Test call placed successfully');
        onClose();
      } else {
        toast.error(result.data.message || 'Test call failed');
        setPhase('idle');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || 'Test call failed');
      setPhase('idle');
    }
  };

  const handleCall = () => {
    const cleaned = toNumber.replace(/\s+/g, '');
    if (!cleaned) return;

    if (!webrtc?.startTestCall) {
      handleHttpFallback(cleaned);
      return;
    }

    if (status === 'loading' || status === 'connecting') {
      toast.error('WebRTC is still initializing. Please wait a moment.');
      return;
    }
    if (status === 'error') {
      toast.error('WebRTC is not available. Please refresh the page.');
      return;
    }

    setPhase('ringing');
    webrtc.startTestCall(fromNumber, cleaned);
  };

  const handleEndCall = () => {
    webrtc?.hangUpTestCall?.();
    setPhase('ended');
  };

  const handleClose = () => {
    if (phase === 'ringing' || phase === 'in-call') {
      webrtc?.hangUpTestCall?.();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-[340px]"
        onClick={(e) => e.stopPropagation()}
      >
        {phase === 'idle' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-primary" />
                <h3 className="text-sm font-semibold">Test Call</h3>
              </div>
              <button type="button" onClick={handleClose} className="p-1 rounded hover:bg-muted">
                <XMarkIcon className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-1">From</p>
            <p className="text-sm font-semibold mb-4">{fromNumber}</p>
            <p className="text-xs text-muted-foreground mb-1">To (destination number)</p>
            <input
              autoFocus
              type="tel"
              placeholder="+1 555 000 0000"
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCall()}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background mb-4 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {webrtc && (status === 'loading' || status === 'connecting') && (
              <p className="text-xs text-amber-500 mb-3 flex items-center gap-1.5">
                <LoadingSpinner size="sm" />
                Initializing microphone…
              </p>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={handleClose} className="flex-1 px-3 py-2 text-sm rounded-lg border border-border hover:bg-muted">
                Cancel
              </button>
              <button
                type="button"
                disabled={!toNumber.trim()}
                onClick={handleCall}
                className="flex-1 px-3 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <PhoneIcon className="h-4 w-4" />
                Call
              </button>
            </div>
          </>
        )}

        {phase === 'ringing' && (
          <div className="text-center py-4">
            <p className="text-base font-semibold">Calling…</p>
            <p className="text-sm text-muted-foreground mt-1 font-mono">{toNumber}</p>
            <button type="button" onClick={handleEndCall} className="mt-4 px-6 py-2 rounded-xl bg-red-600 text-white text-sm">
              Cancel
            </button>
          </div>
        )}

        {phase === 'in-call' && (
          <>
            <div className="flex justify-between mb-4">
              <h3 className="text-sm font-semibold text-green-600">Connected</h3>
              <span className="text-sm font-mono text-muted-foreground">{formatDuration(seconds)}</span>
            </div>
            <p className="text-center text-sm font-mono mb-4">{toNumber}</p>
            <div className="flex gap-3 justify-center">
              <button type="button" onClick={() => webrtc?.toggleMute?.()} className="px-4 py-2 rounded-xl border text-xs">
                {isMuted ? <MicrophoneSolidIcon className="h-5 w-5" /> : <MicrophoneIcon className="h-5 w-5" />}
              </button>
              <button type="button" onClick={handleEndCall} className="px-4 py-2 rounded-xl bg-red-600 text-white text-xs">
                <PhoneXMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </>
        )}

        {phase === 'ended' && (
          <div className="text-center py-4">
            <CheckCircleIcon className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="font-semibold">Call Ended</p>
            {seconds > 0 && <p className="text-sm text-muted-foreground mt-1">Duration: {formatDuration(seconds)}</p>}
            <button type="button" onClick={onClose} className="mt-4 px-6 py-2 rounded-lg border text-sm">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestCallModal;
